import React, { useState } from "react";
import { Button } from "antd";
import { describeAmountDue, formatAmountDue } from "./amountDue";
import { billingApi } from "./billingApi";
import { BillingCode, describeBillingError } from "./billingCodes";
import { describeCard } from "./cards";
import { readDecline, settlePayment } from "./paymentAction";
import "./index.scss";

/**
 * The balance an annual plan left when it ended unpaid (reason OUTSTANDING_DEBT),
 * paid with the card on file (pay-outstanding). The amount is said first, and the
 * button is the confirmation. Paying it settles the debt and nothing else: the
 * subscription stays ended, and the plans come next.
 *
 * What comes of it is the billing page's, as with any fix: `onResolved` gets the
 * status it left and `{ error }`, the bank's refusal — after which part of the
 * balance may be paid, and the status says what is left. With no card on file
 * after all (409 PAYMENT_METHOD_REQUIRED), the card form takes over
 * (`onUseDifferentCard`); with nothing to pay for, `onStale` reads the status
 * again.
 */
function SettleDebtPanel({ organizationId, status, onResolved, onUseDifferentCard, onStale }) {
   const [isPaying, setIsPaying] = useState(false);
   const [error, setError] = useState(null);

   const amount = formatAmountDue(status);
   const sentence = describeAmountDue(status);
   const card = describeCard(status?.paymentMethod);

   const pay = async () => {
      if (isPaying) return;

      setIsPaying(true);
      setError(null);

      try {
         const next = await billingApi.payOutstanding({ organizationId });
         const settled = await settlePayment(next, organizationId);
         onResolved(settled.status, { error: settled.error });
      } catch (failure) {
         const decline = readDecline(failure);

         if (decline) {
            onResolved(decline.status, { error: decline.error });
         } else if (failure.code === BillingCode.PAYMENT_METHOD_REQUIRED) {
            onUseDifferentCard();
         } else if (failure.code === BillingCode.NO_SUBSCRIPTION) {
            onStale();
         } else {
            setError(describeBillingError(failure));
         }
      } finally {
         setIsPaying(false);
      }
   };

   return (
      <div className="billing-panel">
         {sentence && <p className="billing-amount-due">{sentence}</p>}
         <p className="billing-panel__text">
            {card ? `It's charged to your card on file: ${card}.` : "It's charged to your card on file."}
         </p>

         {error && (
            <div className="billing-form__message" role="alert">
               {error}
            </div>
         )}

         <div className="billing-actions">
            <Button className="defaultButton" onClick={onUseDifferentCard} disabled={isPaying}>
               Use a different card
            </Button>
            <Button className="kapSurveyPrimaryButton" onClick={pay} loading={isPaying} disabled={!amount}>
               {amount ? `Pay outstanding balance (${amount})` : "Pay outstanding balance"}
            </Button>
         </div>
      </div>
   );
}

export default SettleDebtPanel;
